import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Fulfillment, UserProfile } from 'api';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { ModelTeamSwitch } from 'dashboard409a/components/ModelTeamSwitch';
import { ConditionalLoader, Section, ValuationStatusTracker } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import { FulfillmentQueryKeys } from 'dashboard409a/services/fulfillment';
import { ModelsQueryKeys } from 'dashboard409a/services/models';
import { AssignUsersDialog } from './components';
import { FulfillmentListResponse, modelStatusMap, reverseModelStatusMap } from './ModelStatus.constants';
import { ModelStatusProps } from './ModelStatus.props';
import {
  AvatarRow,
  BlurOverlay,
  BlurredContent,
  Container,
  CreateModelSupportButton,
  DateRow,
  FooterBox,
  OverlayContainer,
  RoleBox,
  SpacedContainer,
  Wrapper,
} from './ModelStatus.styles';

const ModelStatus = ({
  fulfillmentData,
  isLoading,
  onCreateModelSupport,
  isCreatingModelSupport = false,
  companyId,
  valuationId,
  companyMeasurementDateId,
  readOnly = false,
  isSlcModel,
  onSlcModelChange,
}: ModelStatusProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [_, setIsSaving] = useState(false);
  const [users, setUsers] = useState<{ reviewers: UserProfile[]; preparers: UserProfile[] }>({
    reviewers: [],
    preparers: [],
  });
  const reviewerButtonRef = useRef<HTMLButtonElement>(null);
  const preparerButtonRef = useRef<HTMLButtonElement>(null);
  const [anchorButtonRef, setAnchorButtonRef] = useState<HTMLButtonElement | null>(null);

  // Get the current fulfillment data that matches the company measurement date ID
  const currentFulfillment = useMemo(() => {
    if (!fulfillmentData?.results) {
      return undefined;
    }

    // If we have a specific company measurement date ID, find the matching fulfillment
    if (companyMeasurementDateId) {
      return fulfillmentData.results.find(
        fulfillment => fulfillment.company_measurement_date === companyMeasurementDateId
      );
    }

    // Fallback to first fulfillment
    return fulfillmentData.results[0];
  }, [fulfillmentData, companyMeasurementDateId]);

  // Determine if model support exists based on whether fulfillment exists and has model_status
  const hasModelSupport = useMemo(
    () => !!currentFulfillment && currentFulfillment.model_status !== undefined,
    [currentFulfillment]
  );

  useEffect(() => {
    if (currentFulfillment) {
      setUsers({
        reviewers: (currentFulfillment.reviewers as UserProfile[]) || [],
        preparers: (currentFulfillment.preparers as UserProfile[]) || [],
      });
    }
  }, [currentFulfillment]);

  // Helper function to get user display name
  const getUserDisplayName = useCallback(
    (user: UserProfile): string =>
      user.first_name ? `${user.first_name} ${user.last_name}` : user.email || 'Unknown User',
    []
  );

  const [tempFormData, setTempFormData] = useState<Fulfillment>(
    () =>
      ({
        ...currentFulfillment,
        model_status: currentFulfillment?.model_status ?? modelStatusMap[ModelStatusEnum.DRAFT],
      }) as Fulfillment
  );

  // Sync tempFormData with currentFulfillment when it changes
  useEffect(() => {
    if (currentFulfillment) {
      setTempFormData({
        ...currentFulfillment,
        model_status: currentFulfillment.model_status ?? modelStatusMap[ModelStatusEnum.DRAFT],
      });
    }
  }, [currentFulfillment]);

  // Helper function to update both caches with the updated fulfillment data
  const updateCaches = useCallback(
    (
      updatedFulfillmentData: Partial<Fulfillment> & {
        reviewerUsers?: UserProfile[];
        preparerUsers?: UserProfile[];
      }
    ) => {
      const { reviewerUsers, preparerUsers, ...updateFields } = updatedFulfillmentData;

      // Helper to build the updated fulfillment object
      const buildUpdatedFulfillment = (fulfillment: Fulfillment) => {
        // If we have full user objects provided, use them directly
        // Otherwise, keep the existing reviewers/preparers from the fulfillment
        const updatedReviewers = reviewerUsers && reviewerUsers.length > 0 ? reviewerUsers : fulfillment.reviewers;

        const updatedPreparers = preparerUsers && preparerUsers.length > 0 ? preparerUsers : fulfillment.preparers;

        return {
          ...fulfillment,
          ...updateFields,
          reviewers: updatedReviewers,
          preparers: updatedPreparers,
        };
      };

      // Update the fulfillment list cache (used by ModelStatus in the Modal)
      queryClient.setQueriesData(
        { queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, companyId], exact: false },
        (oldData: FulfillmentListResponse) => {
          if (!oldData?.results || !Array.isArray(oldData.results)) {
            return oldData;
          }

          const updatedResults = oldData.results.map((fulfillment: Fulfillment) => {
            if (
              fulfillment.id === currentFulfillment?.id ||
              fulfillment.company_measurement_date === currentFulfillment?.company_measurement_date
            ) {
              return buildUpdatedFulfillment(fulfillment);
            }
            return fulfillment;
          });

          return {
            ...oldData,
            results: updatedResults,
          };
        }
      );

      // Update the models list cache (used by ModelsTable)
      queryClient.setQueriesData(
        { queryKey: [ModelsQueryKeys.MODELS_LIST], exact: false },
        (oldData: FulfillmentListResponse) => {
          if (!oldData?.results || !Array.isArray(oldData.results)) {
            return oldData;
          }

          const updatedResults = oldData.results.map((fulfillment: Fulfillment) => {
            if (
              fulfillment.id === currentFulfillment?.id ||
              fulfillment.company_measurement_date === currentFulfillment?.company_measurement_date
            ) {
              return buildUpdatedFulfillment(fulfillment);
            }
            return fulfillment;
          });

          return {
            ...oldData,
            results: updatedResults,
          };
        }
      );
    },
    [companyId, currentFulfillment, queryClient]
  );

  const handleSave = useCallback(
    async (overrideData?: {
      supporting_due_date?: string | null;
      model_status?: number;
      reviewerUsers?: UserProfile[];
      preparerUsers?: UserProfile[];
    }) => {
      try {
        if (!currentFulfillment?.id || !companyId || !valuationId) {
          throw new Error('Missing required data to save fulfillment');
        }

        setIsSaving(true);

        // Extract reviewer and preparer IDs from override or currentFulfillment
        const reviewers = overrideData?.reviewerUsers ?? ((currentFulfillment.reviewers as UserProfile[]) || []);
        const preparers = overrideData?.preparerUsers ?? ((currentFulfillment.preparers as UserProfile[]) || []);
        const reviewerIds = reviewers
          .map((user: UserProfile) => user.id)
          .filter((id): id is number => id !== undefined);
        const preparerIds = preparers
          .map((user: UserProfile) => user.id)
          .filter((id): id is number => id !== undefined);

        const updateData: Partial<Fulfillment> = {
          supporting_due_date: overrideData?.supporting_due_date ?? tempFormData.supporting_due_date,
          model_status: overrideData?.model_status ?? tempFormData.model_status,
          reviewers: reviewerIds.length > 0 ? reviewerIds : undefined,
          preparers: preparerIds.length > 0 ? preparerIds : undefined,
        };

        await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
          companyId,
          valuationId,
          updateData as Fulfillment
        );

        // Update both caches with the new data
        // Exclude reviewers/preparers from updateData since we handle them separately with reviewerUsers/preparerUsers
        const cacheUpdateData: Partial<Fulfillment> = {
          supporting_due_date: updateData.supporting_due_date,
          model_status: updateData.model_status,
        };
        updateCaches({
          ...cacheUpdateData,
          reviewerUsers: overrideData?.reviewerUsers,
          preparerUsers: overrideData?.preparerUsers,
        });

        // Invalidate the valuation detail query to refresh the OperationsModal
        if (valuationId) {
          queryClient.invalidateQueries({
            queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
            exact: false,
          });
        }
        queryClient.invalidateQueries({
          queryKey: [ModelsQueryKeys.MODELS_LIST],
          exact: false,
        });

        enqueueSnackbar('Fulfillment saved successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to save fulfillment. Please try again.', { variant: 'error' });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [companyId, currentFulfillment, valuationId, tempFormData, updateCaches, enqueueSnackbar, queryClient]
  );

  const handleSupportingDueDateChange = async (date: Date | null) => {
    // Convert to YYYY-MM-DD format for the API
    const formattedDate = date && !Number.isNaN(date.getTime()) ? date.toISOString().split('T')[0] : null;
    setTempFormData(prev => ({ ...prev, supporting_due_date: formattedDate }));
    // Auto-save after date change
    await handleSave({ supporting_due_date: formattedDate });
  };

  const handleModelStatusChange = async (statusId: string) => {
    // Convert UI status string to API numeric value using the map
    const apiStatusValue = modelStatusMap[statusId as keyof typeof modelStatusMap];
    if (apiStatusValue !== undefined) {
      const statusValue = apiStatusValue as Fulfillment.model_status;
      setTempFormData(prev => ({ ...prev, model_status: statusValue }));
      // Auto-save after status change
      await handleSave({ model_status: statusValue });
    }
  };

  // Helper functions for display text
  const getReviewerDisplayText = () => {
    const reviewerUsers = currentFulfillment?.reviewers || [];
    if (reviewerUsers.length === 0) {
      return 'No Reviewers Assigned';
    }

    return reviewerUsers.map(user => getUserDisplayName(user as UserProfile)).join(', ');
  };

  const getPreparerDisplayText = () => {
    const preparerUsers = currentFulfillment?.preparers || [];
    if (preparerUsers.length === 0) {
      return 'No Preparers Assigned';
    }

    return preparerUsers.map(user => getUserDisplayName(user as UserProfile)).join(', ');
  };

  const handleAssignUsers = async (reviewerUsers: UserProfile[], preparerUsers: UserProfile[]) => {
    // Use handleSave to save reviewers and preparers (which also updates caches)
    await handleSave({ reviewerUsers, preparerUsers });
  };

  return (
    <Section
      title="Model Status"
      headerAction={
        onSlcModelChange && <ModelTeamSwitch checked={isSlcModel ?? false} onChange={onSlcModelChange} />
      }>
      <OverlayContainer>
        <BlurredContent hasModelSupport={hasModelSupport} readOnly={readOnly}>
          <Container>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <Typography fontWeight={600}>{currentFulfillment?.company_name}</Typography>
            </Box>
            <Typography fontWeight={600} mb={1}>
              Supporting Due Date
            </Typography>
            <DateRow>
              <ConditionalLoader isLoading={isLoading} width={200}>
                <MobileDatePicker
                  value={
                    tempFormData?.supporting_due_date ? new Date(`${tempFormData.supporting_due_date}T00:00:00`) : null
                  }
                  onChange={handleSupportingDueDateChange}
                  format="MM/dd/yyyy"
                  disabled={readOnly}
                />
              </ConditionalLoader>
              <CalendarMonthIcon fontSize="small" color="primary" />
            </DateRow>

            <SpacedContainer mt={1}>
              <ValuationStatusTracker
                statusOptions={Object.entries(reverseModelStatusMap).map(([, uiValue]) => ({
                  id: uiValue,
                  label: uiValue,
                }))}
                currentStatusId={
                  reverseModelStatusMap[
                    tempFormData.model_status ??
                      (modelStatusMap[ModelStatusEnum.DRAFT] as keyof typeof reverseModelStatusMap)
                  ] || ModelStatusEnum.DRAFT
                }
                onStatusChange={readOnly ? undefined : handleModelStatusChange}
                isLoading={isLoading}
                disabled={readOnly}
              />
            </SpacedContainer>

            <FooterBox>
              <RoleBox>
                <Wrapper>
                  <Typography fontWeight={600}>Reviewer</Typography>
                  {hasModelSupport && !readOnly && (
                    <Tooltip title="Assign Reviewer">
                      <IconButton
                        ref={reviewerButtonRef}
                        size="small"
                        onClick={() => {
                          setAnchorButtonRef(reviewerButtonRef.current);
                          setIsAssignDialogOpen(true);
                        }}
                        disabled={isLoading}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Wrapper>
                <AvatarRow>
                  <Avatar src="" name={getReviewerDisplayText()} roleTitle="Analyst" isLoading={isLoading} />
                </AvatarRow>
              </RoleBox>

              <RoleBox>
                <Wrapper>
                  <Typography fontWeight={600}>Preparer</Typography>
                  {hasModelSupport && !readOnly && (
                    <Tooltip title="Assign Preparer">
                      <IconButton
                        ref={preparerButtonRef}
                        size="small"
                        onClick={() => {
                          setAnchorButtonRef(preparerButtonRef.current);
                          setIsAssignDialogOpen(true);
                        }}
                        disabled={isLoading}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Wrapper>
                <AvatarRow>
                  <Avatar src="" name={getPreparerDisplayText()} roleTitle="Analyst" isLoading={isLoading} />
                </AvatarRow>
              </RoleBox>
            </FooterBox>
          </Container>
        </BlurredContent>

        {!hasModelSupport && !readOnly && (
          <BlurOverlay>
            <Typography variant="h6" fontWeight={600} color="text.primary" textAlign="center">
              Model Support Required
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 300 }}>
              A model support needs to be created before this area becomes available
            </Typography>
            <CreateModelSupportButton
              onClick={onCreateModelSupport}
              variant="contained"
              disabled={isCreatingModelSupport}>
              {isCreatingModelSupport ? 'Creating...' : 'Create Model Support'}
            </CreateModelSupportButton>
          </BlurOverlay>
        )}
      </OverlayContainer>
      <AssignUsersDialog
        open={isAssignDialogOpen}
        anchorEl={anchorButtonRef}
        onClose={() => {
          setIsAssignDialogOpen(false);
          setAnchorButtonRef(null);
        }}
        onSave={handleAssignUsers}
        getUserDisplayName={getUserDisplayName}
        fulfillmentData={currentFulfillment}
        title="Assign Reviewers and Preparers"
        availableUsers={users}
      />
    </Section>
  );
};

export default ModelStatus;
